import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, MenuItem, Select, FormControl, InputLabel, TextField, Button, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { C, fmtUSD } from "./reportingUtils";

// Metrics an alert rule can watch. Each computes a single number from the portfolio.
export const ALERT_METRICS = [
  { key: "avgScore", label: "Average ARC score", fmt: (v) => v.toFixed(1), compute: (rows) => { const s = rows.filter((r) => r.score != null); return s.length ? s.reduce((a, r) => a + r.score, 0) / s.length : 0; } },
  { key: "highRiskPct", label: "High-risk share (%)", fmt: (v) => `${v.toFixed(1)}%`, compute: (rows) => { const s = rows.filter((r) => r.score != null); return s.length ? (s.filter((r) => r.score >= 6.6).length / s.length) * 100 : 0; } },
  { key: "pending", label: "Pending review (count)", fmt: (v) => `${Math.round(v)}`, compute: (rows) => rows.filter((r) => String(r.status).toUpperCase() === "PENDING").length },
  { key: "totalExposure", label: "Total recommended exposure ($)", fmt: (v) => fmtUSD(v), compute: (rows) => rows.reduce((a, r) => a + r.recLimit, 0) },
  { key: "count", label: "Counterparties (count)", fmt: (v) => `${Math.round(v)}`, compute: (rows) => rows.length },
];
const OPS = [{ key: ">", label: "greater than" }, { key: ">=", label: "at least" }, { key: "<", label: "less than" }, { key: "<=", label: "at most" }];
const LS_KEY = "crediarc_bi_alert_rules";
const DEFAULTS = [
  { id: "d1", metric: "highRiskPct", op: ">", threshold: 15 },
  { id: "d2", metric: "avgScore", op: ">", threshold: 6.5 },
];

export const loadRules = () => { try { const r = JSON.parse(localStorage.getItem(LS_KEY) || "null"); return Array.isArray(r) ? r : DEFAULTS; } catch { return DEFAULTS; } };
const saveRules = (r) => { try { localStorage.setItem(LS_KEY, JSON.stringify(r)); } catch { /* ignore */ } };
const opTest = (v, op, t) => (op === ">" ? v > t : op === ">=" ? v >= t : op === "<" ? v < t : v <= t);

// Pure evaluator the parent can reuse for a header banner.
export const evaluateRules = (rows, rules) => rules.map((rule) => {
  const m = ALERT_METRICS.find((x) => x.key === rule.metric);
  if (!m) return null;
  const value = m.compute(rows || []);
  return { rule, metric: m, value, breached: opTest(value, rule.op, parseFloat(rule.threshold)) };
}).filter(Boolean);

const AlertsPanel = ({ rows, onRulesChange }) => {
  const [rules, setRules] = useState(loadRules());
  const [metric, setMetric] = useState("highRiskPct");
  const [op, setOp] = useState(">");
  const [threshold, setThreshold] = useState("10");

  useEffect(() => { onRulesChange?.(rules); }, [rules, onRulesChange]);

  const results = useMemo(() => evaluateRules(rows, rules), [rows, rules]);

  const add = () => {
    if (threshold === "" || isNaN(parseFloat(threshold))) return;
    const next = [...rules, { id: `${Date.now()}`, metric, op, threshold: parseFloat(threshold) }];
    setRules(next); saveRules(next);
  };
  const remove = (id) => { const next = rules.filter((r) => r.id !== id); setRules(next); saveRules(next); };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Box sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", p: "16px 18px" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink, mb: 0.5 }}>Threshold alerts</Typography>
        <Typography sx={{ fontSize: 12.5, color: C.muted, mb: 1.5 }}>Get flagged when a portfolio metric crosses a threshold. Rules are evaluated live here; email &amp; scheduled delivery are on the roadmap.</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
          <Typography sx={{ fontSize: 13.5, color: C.slate }}>Notify me when</Typography>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel sx={{ fontSize: 13 }}>Metric</InputLabel>
            <Select label="Metric" value={metric} onChange={(e) => setMetric(e.target.value)} sx={{ fontSize: 13.5, background: "#fff" }}>
              {ALERT_METRICS.map((m) => <MenuItem key={m.key} value={m.key} sx={{ fontSize: 13.5 }}>{m.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel sx={{ fontSize: 13 }}>is</InputLabel>
            <Select label="is" value={op} onChange={(e) => setOp(e.target.value)} sx={{ fontSize: 13.5, background: "#fff" }}>
              {OPS.map((o) => <MenuItem key={o.key} value={o.key} sx={{ fontSize: 13.5 }}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Threshold" size="small" value={threshold} onChange={(e) => setThreshold(e.target.value)} sx={{ width: 110 }} inputProps={{ inputMode: "decimal" }} />
          <Button startIcon={<AddIcon />} variant="contained" onClick={add} sx={{ textTransform: "none", background: C.blue, "&:hover": { background: "#2455c0" } }}>Add alert</Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {results.length === 0 && <Typography sx={{ fontSize: 13, color: C.muted, textAlign: "center", py: 3 }}>No alerts configured yet.</Typography>}
        {results.map(({ rule, metric: m, value, breached }) => {
          const opLabel = OPS.find((o) => o.key === rule.op)?.label || rule.op;
          return (
            <Box key={rule.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, background: "#fff",
              border: `1px solid ${breached ? "#F3C6C6" : C.line}`, borderLeft: `4px solid ${breached ? C.red : C.green}`, borderRadius: "10px", p: "12px 16px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                {breached ? <NotificationsActiveIcon sx={{ color: C.red }} /> : <CheckCircleOutlineIcon sx={{ color: C.green }} />}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, color: C.ink, fontWeight: 600 }}>{m.label} {opLabel} {rule.threshold}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: C.muted }}>Current: <b style={{ color: breached ? C.red : C.slate }}>{m.fmt(value)}</b> · {breached ? "Breached" : "Within threshold"}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {breached && <Chip label="ALERT" size="small" sx={{ height: 20, fontWeight: 700, fontSize: 10.5, background: "#FDECEC", color: C.red, letterSpacing: ".05em" }} />}
                <Button size="small" onClick={() => remove(rule.id)} sx={{ minWidth: 0, color: C.muted }}><DeleteOutlineIcon fontSize="small" /></Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AlertsPanel;
